import { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { makeStyles } from '@material-ui/core/styles';
import { Typography, Stepper, Step } from '@material-ui/core';
import SurveyInfo from './surveyInfo';
import AttributeQuestion from '../../attributeQuestion';
import QuestionnaireQuestion from '../../questionnaireQuestion';
import ReviewFeedbackForm from '../../reviewFeedbackForm';
import CustomButton from '../../customButton';
import CustomFormDrawer from '../../customFormDrawer';
import ReviewDescription from '../reviewDescription';
import { isRichTextEditorEmpty } from '../../../../utility/validation';
import {
  getItemById,
  isArray,
  isArrayEmpty,
} from '../../../../utility/helpers';
import {
  isQuestionCompleted,
  hasErrors,
  getAnswerCommentFields,
  getCommentInitialData,
  getRankingOptionIds,
} from '../../../../utility/fillSurveyUtils';
import { QUESTIONNAIRE_QUESTION_TYPES } from '../../../../constants/survey';

const useStyles = makeStyles(
  ({ breakpoints, palette: { primary }, spacing }) => ({
    stepperWrapper: {
      display: 'flex',
      alignItems: 'center',
      width: '100%',
    },
    stepper: {
      flexGrow: 1,
    },
    step: {
      backgroundColor: primary.bluish7,
      borderRadius: 30,
      flexGrow: 1,
      flexShrink: 0,
      height: 6,
      padding: 0,
      marginRight: spacing(1),
      [breakpoints.up('sm')]: {
        marginRight: spacing(1.5),
        height: 8,
      },
    },
    completedStep: {
      backgroundColor: primary.bluish1,
    },
    stepperLabelWrapper: {
      display: 'flex',
      alignItems: 'center',
      flexShrink: 0,
      width: 25,
    },
    stepperLabel: {
      color: primary.bluish3,
      fontSize: 10,
      lineHeight: '12px',
      [breakpoints.up('sm')]: {
        fontSize: 12,
        lineHeight: '14px',
      },
    },
    stepContent: {
      height: '100%',
      overflow: 'hidden',
      marginTop: spacing(4),
      [breakpoints.up('sm')]: {
        marginTop: spacing(6),
        height: 'auto',
        maxHeight: '100%',
      },
    },
    stepFooter: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingTop: spacing(4),
      marginBottom: spacing(4),
      flexShrink: 0,
      [breakpoints.up('sm')]: {
        paddingTop: spacing(10),
        marginBottom: 0,
      },
    },
    button: {
      padding: spacing(1, 2),
      '& span': {
        fontFamily: 'ProximaNova-Bold',
        fontSize: 12,
        lineHeight: '24px',
        letterSpacing: 'normal',
        textTransform: 'none',
      },
      [breakpoints.up('sm')]: {
        padding: spacing(2, 4),
        '& span': {
          fontSize: 16,
          lineHeight: '24px',
        },
      },
    },
    backButton: {
      padding: spacing(1, 0),
      marginLeft: spacing(-2.25),
      '& span': {
        textTransform: 'capitalize',
        marginLeft: 0,
      },
      [breakpoints.up('sm')]: {
        padding: spacing(2, 0),
      },
    },
    backIconWrapper: {
      marginRight: 0,
    },
    footerNextBlock: {
      marginLeft: 'auto',
    },
    addCommentButton: {
      color: primary.bluish1,
      marginRight: spacing(2),
      '&:disabled': {
        border: `1px solid ${primary.bluish7}`,
        backgroundColor: primary.white,
        '& span': {
          color: primary.bluish7,
          '& img': {
            opacity: 0.2,
          },
        },
      },
    },
    nextButton: {
      border: `1px solid ${primary.blue2}`,
      '&:disabled': {
        backgroundColor: primary.blue5,
        border: `1px solid ${primary.blue5}`,
      },
    },
    disabledCommentButton: {
      cursor: 'default',
      pointerEvents: 'none',
      opacity: '50%',
    },
    addCommentIconWrapper: {
      marginRight: 0,
    },
  })
);

const SurveySteps = ({
  translations,
  survey,
  review,
  steps,
  activeStep,
  surveyStep,
  selectedSubject = {},
  isPreview = false,
  isAddCommentDialogOpened = false,
  hasAddCommentButton = false,
  feedbackErrors = {},
  answerReplyErrors = {},
  onSaveComment = () => {},
  onSetAnswerReplyErrors,
  onSetFeedbackErrors,
  onSetFeedbackComments,
  onAttributeQuestionAnswerSelect,
  onAttributeQuestionAnswerChange,
  onQuestionnaireQuestionOptionSelect,
  onQuestionnaireQuestionAnswerChange,
  onSubmit,
  onStepChange,
  onGoBack,
  onAddComment = () => {},
}) => {
  const classes = useStyles();
  const [rankingDrafts, setRankingDrafts] = useState({});

  const { isSingleSurvey, questionnaire } = survey;
  const isQuestionnaire = questionnaire != null;
  const {
    results,
    subjects,
    isVertical,
    hasNeutralAnswer,
    hasAdditionalFeedback,
    withTextReplyQuestions,
    isEnps,
  } = review;
  const hasVisibleDescription = isQuestionnaire
    ? isRichTextEditorEmpty(questionnaire?.description)
    : isRichTextEditorEmpty(review?.visible_description);
  const currentQuestion = hasVisibleDescription
    ? review.questions[activeStep - 1]
    : review.questions[activeStep];
  const isEnpsOpenEndedQuestion =
    isEnps && isArrayEmpty(currentQuestion?.answers);
  const questionWithTextReply =
    currentQuestion?.text_reply_questions ?? withTextReplyQuestions;
  const [subject] = subjects;
  const numberOfSteps = steps.length;
  const answerComment = useMemo(() => {
    return isSingleSurvey
      ? subjects?.[0]?.replied?.[currentQuestion?.id]
      : getItemById(subjects, selectedSubject?.id)?.replied[
          currentQuestion?.id
        ];
  }, [isSingleSurvey, currentQuestion, subjects, selectedSubject]);
  const isEditComment = !!answerComment?.reply;
  const isMobile = window.innerWidth < 768;
  const hasSelectedAnswer =
    results[subject?.review_tracking]?.[currentQuestion?.id];

  const isLastStep = activeStep + 1 === numberOfSteps;
  const isVisibleDescription = hasVisibleDescription
    ? Boolean(activeStep)
    : true;
  const isNextDisabled =
    ((hasAdditionalFeedback || isEnpsOpenEndedQuestion
      ? !isLastStep
      : !hasAdditionalFeedback) &&
      !isQuestionCompleted(
        results,
        subjects,
        currentQuestion?.id,
        currentQuestion?.type
      ) &&
      isVisibleDescription) ||
    hasErrors(answerReplyErrors) ||
    hasErrors(feedbackErrors);
  const submittButtonLabel = useMemo(() => {
    return isLastStep ? translations.submitLabel : translations.nextLabel;
  }, [translations, isLastStep]);
  const commentButtonLabel = useMemo(() => {
    if (isMobile) return '';

    return isEditComment
      ? translations.editCommentLabel
      : translations.addCommentLabel;
  }, [translations, isMobile, isEditComment]);
  const commentsFormTitle = useMemo(() => {
    return isEditComment
      ? translations.addCommentForm.title.edit
      : translations.addCommentForm.title.add;
  }, [translations, isEditComment]);

  const initialComment = useMemo(
    () => getCommentInitialData(answerComment),
    [answerComment]
  );
  const answerCommentField = getAnswerCommentFields();

  const handleRankingDraftChange = useCallback((questionId, user, ids) => {
    setRankingDrafts(prevDrafts => ({
      ...prevDrafts,
      [questionId]: {
        ...prevDrafts[questionId],
        [user.review_tracking]: ids,
      },
    }));
  }, []);

  const persistRankingThen = async callback => {
    if (
      isPreview ||
      currentQuestion?.type !== QUESTIONNAIRE_QUESTION_TYPES.RANKING
    ) {
      return callback();
    }

    await Promise.all(
      subjects.map(currentSubject => {
        const saved =
          results?.[currentSubject.review_tracking]?.[currentQuestion.id]
            ?.ranked_option_ids;

        if (isArray(saved) && !isArrayEmpty(saved)) {
          return Promise.resolve();
        }

        const ids =
          rankingDrafts[currentQuestion.id]?.[currentSubject.review_tracking] ??
          getRankingOptionIds(currentQuestion.options);

        return onQuestionnaireQuestionAnswerChange(
          currentQuestion.id,
          currentQuestion.type,
          ids,
          currentSubject
        );
      })
    );

    return callback();
  };

  const handleAddComment = () => onAddComment(subject);

  const handleCloseAddCommentDrawer = () => onAddComment();

  const handleSaveComment = currentAnswerComment =>
    onSaveComment(currentQuestion?.id, currentAnswerComment);

  const handleDeleteComment = () => {
    onSaveComment(currentQuestion?.id, { answerComment: undefined });
    onAddComment();
  };

  const renderStepQuestion = () => {
    if (isQuestionnaire) {
      return (
        <QuestionnaireQuestion
          key={currentQuestion?.id}
          translations={translations.answers}
          question={currentQuestion}
          subjects={subjects}
          results={results}
          answerReplyErrors={answerReplyErrors}
          isSingleReview={isSingleSurvey}
          isPreview={isPreview}
          onSetAnswerReplyErrors={onSetAnswerReplyErrors}
          onAnswerSelect={onQuestionnaireQuestionOptionSelect}
          onAnswerChange={onQuestionnaireQuestionAnswerChange}
          onDraftChange={handleRankingDraftChange}
        />
      );
    }

    if (isLastStep && hasAdditionalFeedback && !isEnpsOpenEndedQuestion) {
      return (
        <ReviewFeedbackForm
          translations={translations.feedbackForm}
          initialComments={review.comments}
          subjects={subjects}
          isSingleReview={isSingleSurvey}
          isPreview={isPreview}
          errors={feedbackErrors}
          setErrors={onSetFeedbackErrors}
          setComments={onSetFeedbackComments}
        />
      );
    }

    return (
      <AttributeQuestion
        key={currentQuestion?.id}
        translations={translations.answers}
        isVertical={isVertical}
        question={currentQuestion}
        results={results}
        answerReplyErrors={answerReplyErrors}
        isSingleReview={isSingleSurvey}
        isEnpsOpenEndedQuestion={isEnpsOpenEndedQuestion}
        hasNeutralAnswer={hasNeutralAnswer}
        withTextReplyQuestions={questionWithTextReply}
        isPreview={isPreview}
        hasAddCommentButton={review?.hasAnswerComment}
        subjects={subjects}
        onSetAnswerReplyErrors={onSetAnswerReplyErrors}
        onAddComment={onAddComment}
        onAnswerSelect={onAttributeQuestionAnswerSelect}
        onAnswerChange={onAttributeQuestionAnswerChange}
      />
    );
  };

  return (
    <>
      <SurveyInfo survey={survey} surveyStep={surveyStep} />
      <div className={classes.stepperWrapper}>
        <Stepper
          classes={{ root: classes.stepper }}
          activeStep={activeStep}
          connector={null}
        >
          {steps.map((step, index) => {
            return (
              <Step
                key={`step_item_${step.id}`}
                classes={{
                  horizontal: classes.step,
                  completed: classes.completedStep,
                }}
                completed={activeStep >= index}
              />
            );
          })}
        </Stepper>
        <div className={classes.stepperLabelWrapper}>
          <Typography className={classes.stepperLabel} variant="caption">{`${
            activeStep + 1
          }/ `}</Typography>
          <Typography
            className={classes.stepperLabel}
            variant="caption"
          >{`${numberOfSteps}`}</Typography>
        </div>
      </div>
      {!activeStep && hasVisibleDescription ? (
        <ReviewDescription survey={survey} surveyStep={surveyStep} />
      ) : (
        <div className={classes.stepContent}>{renderStepQuestion()}</div>
      )}
      <div className={classes.stepFooter}>
        {!(surveyStep === 0 && activeStep === 0) && (
          <CustomButton
            className={classNames(classes.button, classes.backButton)}
            customIconWrapper={classes.backIconWrapper}
            type="back"
            onClick={onGoBack}
          >
            {translations.backLabel}
          </CustomButton>
        )}
        <div className={classes.footerNextBlock}>
          {hasAddCommentButton &&
            !isQuestionnaire &&
            !(
              (isLastStep && hasAdditionalFeedback) ||
              (!activeStep && hasVisibleDescription)
            ) && (
              <CustomButton
                className={classNames(classes.button, classes.addCommentButton)}
                customIconWrapper={classNames({
                  [classes.addCommentIconWrapper]: isMobile,
                })}
                disabled={isPreview || !hasSelectedAnswer}
                type={
                  isEditComment
                    ? 'editCommentDarkRoundedOutlined'
                    : 'addCommentDarkRoundedOutlined'
                }
                onClick={handleAddComment}
              >
                {commentButtonLabel}
              </CustomButton>
            )}
          <CustomButton
            className={classNames(classes.button, classes.nextButton)}
            type="withTextDarkRounded"
            disabled={isPreview ? false : isNextDisabled}
            onClick={
              isLastStep
                ? () => persistRankingThen(onSubmit)
                : () => persistRankingThen(() => onStepChange(activeStep + 1))
            }
          >
            {submittButtonLabel}
          </CustomButton>
        </div>
      </div>
      {!isQuestionnaire && (
        <CustomFormDrawer
          translations={translations.addCommentForm}
          titleText={commentsFormTitle}
          isOpened={isAddCommentDialogOpened}
          initialData={initialComment}
          isInitialValid={isEditComment}
          fields={answerCommentField}
          onClose={handleCloseAddCommentDrawer}
          onSave={handleSaveComment}
          onDelete={handleDeleteComment}
          isWholeScreen={isMobile}
          isMiddleWidth={!isMobile}
          hasCancelButton
        />
      )}
    </>
  );
};

SurveySteps.propTypes = {
  translations: PropTypes.object.isRequired,
  steps: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  activeStep: PropTypes.number.isRequired,
  survey: PropTypes.shape({}).isRequired,
  review: PropTypes.shape({}).isRequired,
  surveyStep: PropTypes.number.isRequired,
  selectedSubject: PropTypes.object,
  feedbackErrors: PropTypes.shape({}),
  answerReplyErrors: PropTypes.shape({}),
  isPreview: PropTypes.bool,
  isAddCommentDialogOpened: PropTypes.bool,
  hasAddCommentButton: PropTypes.bool,
  onSetAnswerReplyErrors: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onStepChange: PropTypes.func.isRequired,
  onGoBack: PropTypes.func.isRequired,
  onAttributeQuestionAnswerSelect: PropTypes.func.isRequired,
  onAttributeQuestionAnswerChange: PropTypes.func.isRequired,
  onQuestionnaireQuestionOptionSelect: PropTypes.func.isRequired,
  onQuestionnaireQuestionAnswerChange: PropTypes.func.isRequired,
  onSetFeedbackErrors: PropTypes.func.isRequired,
  onSetFeedbackComments: PropTypes.func.isRequired,
  onAddComment: PropTypes.func,
  onSaveComment: PropTypes.func,
};

export default SurveySteps;
